// ADR 8.9.26g §3 — the fourteen raw controls, and the fork that decides
// whether replacing them works at all.
//
// **`Field` takes the control's props; it does not take the control as a
// child.** `CreatePlanPage` already had a local `Field({ label, children })`
// whose callers passed a `<select>` in, and keeping that shape leaves every
// `<select>`, `<input>` and `<textarea>` in the feature file — so AC3 fails by
// construction and remembering to associate the label with the control stays
// the caller's job. So this renders the control itself, from `type` / `value` /
// `onChange` / `options`, and the local `Field` is deleted rather than
// re-pointed.
//
// The association is the point, not a detail: `useId` ties `<label htmlFor>`,
// the control's `id`, and `aria-describedby` on the error to one another, so an
// error is *reachable from the control* by assistive tech rather than merely
// adjacent to it (AC10).
//
// `hideLabel` exists because five call sites labelled their control with
// `aria-label` and showed nothing. Dropping that would put a visible label
// beside every flashcard row; inventing a second labelling mechanism would put
// the association back in the caller's hands. `sr-only` on a real `<label>` is
// both: the accessible name is unchanged and the layout is.

import { Fragment, useId, type ChangeEvent, type HTMLAttributes, type ReactNode } from 'react'

import { FOCUS_RING } from './Button'

/** `checkbox` is deliberately absent — see `CheckboxField` at the foot. */
export type FieldType = 'text' | 'search' | 'email' | 'password' | 'time' | 'select' | 'textarea'

export interface FieldOption {
  value: string
  label: ReactNode
  /** ADR 15.9.26h D6: a locked target row — rendered, never selectable. */
  disabled?: boolean
  /** ADR 3.10.26a §5: consecutive options sharing one render under one native `<optgroup label>`. */
  group?: string
}

/** ADR 30.9.26e §5 — exported so the language picker's trigger shares the outlined look. */
export const FIELD_CONTROL =
  'w-full rounded-control border border-boundary bg-surface px-3 py-2 text-body-lg text-ink ' +
  `${FOCUS_RING} disabled:bg-surface-sunken disabled:opacity-70`

/** A run of consecutive options sharing a `group` (`undefined` for the bare ones). */
interface OptionRun {
  group: string | undefined
  options: FieldOption[]
}

/** ADR 3.10.26a §5 — out of `FieldControl` so the split is not charged to its complexity budget. */
function optionRuns(options: FieldOption[]): OptionRun[] {
  const runs: OptionRun[] = []
  for (const option of options) {
    const last = runs[runs.length - 1]
    if (last !== undefined && last.group === option.group) last.options.push(option)
    else runs.push({ group: option.group, options: [option] })
  }
  return runs
}

function renderOptions(options: FieldOption[]): ReactNode {
  return options.map((option) => (
    <option key={option.value} value={option.value} disabled={option.disabled}>
      {option.label}
    </option>
  ))
}

/** Bare runs render as today; a grouped run sits under its heading line. */
function renderRuns(options: FieldOption[]): ReactNode {
  return optionRuns(options).map((run, index) =>
    run.group === undefined ? (
      <Fragment key={index}>{renderOptions(run.options)}</Fragment>
    ) : (
      <optgroup key={`${index}-${run.group}`} label={run.group}>
        {renderOptions(run.options)}
      </optgroup>
    ),
  )
}

/** One handler shape for all three elements; each carries `target.value`. */
type Change = ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>

interface ControlProps {
  id: string
  type: FieldType
  value: string
  onChange: (value: string) => void
  options: FieldOption[]
  describedBy?: string
  invalid: boolean
  disabled?: boolean
  required?: boolean
  autoComplete?: string
  placeholder?: string
  rows?: number
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode']
}

/**
 * The three elements, spelt out rather than composed through a spread: the
 * `onChange` signature differs by element, and a shared props object typed
 * loosely enough to satisfy all three is exactly how a `<select>` ends up
 * accepting a `placeholder`.
 */
function FieldControl(props: ControlProps) {
  const { id, value, disabled, required, describedBy, invalid } = props
  const onChange = (event: Change) => props.onChange(event.target.value)
  const shared = {
    id,
    value,
    disabled,
    required,
    onChange,
    className: FIELD_CONTROL,
    'aria-describedby': describedBy,
    'aria-invalid': invalid ? true : undefined,
  }

  if (props.type === 'select') {
    return (
      <select {...shared}>{renderRuns(props.options)}</select>
    )
  }
  if (props.type === 'textarea') {
    return <textarea {...shared} rows={props.rows} placeholder={props.placeholder} />
  }
  return (
    <input
      {...shared}
      type={props.type}
      placeholder={props.placeholder}
      autoComplete={props.autoComplete}
      inputMode={props.inputMode}
    />
  )
}

export interface FieldProps {
  label: ReactNode
  value: string
  onChange: (value: string) => void
  type?: FieldType
  /** `select` only; ignored by the other three. */
  options?: FieldOption[]
  /** The server's own words, or `null`. Rendered *and* associated (AC10). */
  error?: string | null
  disabled?: boolean
  required?: boolean
  autoComplete?: string
  placeholder?: string
  rows?: number
  /** `text` inputs only — the on-screen keyboard's layout (ADR 30.9.26y §6: `url`). */
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode']
  /** The label stays in the accessibility tree and leaves the layout. */
  hideLabel?: boolean
  /** Label and control side by side — the tutor composer's two pickers. */
  inline?: boolean
  className?: string
}

/** Out of the component so the branch is not charged to its complexity budget. */
function wrapperClass(inline: boolean, className: string): string {
  return `${inline ? 'flex items-center gap-2' : 'space-y-1'} ${className}`
}

function labelClass(hidden: boolean): string {
  return hidden ? 'sr-only' : 'block shrink-0 text-label-lg text-ink'
}

export function Field({
  label,
  type = 'text',
  options = [],
  error = null,
  hideLabel = false,
  inline = false,
  className = '',
  ...control
}: FieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <div className={wrapperClass(inline, className)}>
      <label htmlFor={id} className={labelClass(hideLabel)}>
        {label}
      </label>
      <FieldControl
        {...control}
        id={id}
        type={type}
        options={options}
        invalid={error !== null}
        describedBy={error === null ? undefined : errorId}
      />
      {error === null ? null : (
        <p id={errorId} role="alert" className="text-body-sm text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

export interface CheckboxFieldProps {
  /** Rich, because the terms line carries a link inside it. */
  label: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  required?: boolean
  /**
   * ADR 14.10.26f §2 — drawn *after* the `<label>`, so it never joins the
   * accessible name, and wired as the checkbox's `aria-describedby` instead.
   */
  hint?: ReactNode
}

/**
 * The one control that is not a `Field`, and it is a separate component rather
 * than a sixth `type` because its state is a boolean, its label sits *after*
 * it, and it has no error line — three differences that would have turned
 * `FieldProps.value` into a union nobody could read.
 *
 * It lives in this file so `<input` still appears only under `web/src/ui/`
 * (AC3): one element, one folder, no exception.
 */
export function CheckboxField({
  label,
  checked,
  onChange,
  disabled = false,
  required = false,
  hint,
}: CheckboxFieldProps) {
  const hintId = useId()
  const hinted = hint !== undefined && hint !== null
  const box = (
    <label className="flex items-start gap-2 text-body-md text-ink">
      <input
        type="checkbox"
        className={`mt-0.5 accent-accent ${FOCUS_RING}`}
        required={required}
        disabled={disabled}
        checked={checked}
        aria-describedby={hinted ? hintId : undefined}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  )
  if (!hinted) return box
  return (
    <div>
      {box}
      <span id={hintId} className="block ps-6 text-body-md text-ink-muted">
        {hint}
      </span>
    </div>
  )
}
